import { UWQueueAgentRow, UWQueueDashboardStats } from "@/models/underwriting";
import { CustomerModel } from "@/models/pipeline";

export function queueCustomerAge(
  customer: Pick<CustomerModel, "allocatedOn" | "daysOld">,
  now = Date.now()
): number | null {
  const allocatedOn = customer.allocatedOn ? Date.parse(customer.allocatedOn) : NaN;
  if (Number.isFinite(allocatedOn)) return Math.max(0, now - allocatedOn);
  return Number.isFinite(customer.daysOld) && customer.daysOld >= 0
    ? customer.daysOld * 86_400_000
    : null;
}

export function formatQueueCustomerAge(age: number | null): string {
  if (age == null) return "\u2014";
  const totalMinutes = Math.floor(age / 60_000);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  if (totalMinutes < 1440) return `${Math.floor(totalMinutes / 60)}h`;
  return `${Math.floor(totalMinutes / 1440)}d`;
}

/**
 * Renders a minute count as the coarsest unit that still reads sensibly:
 * "42m", "3.5h", "2.1d". Queue ages routinely span minutes to days, so a single
 * fixed unit is either unreadable or lossy at one end of the range.
 */
export function formatDuration(minutes: number | null | undefined, zeroLabel = "—"): string {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  if (minutes <= 0 || minutes < 1) return zeroLabel;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

/**
 * Oldest current queue age, computed directly from the visible application
 * list (the same one the Applications table renders) rather than a separate
 * backend aggregate — so it can never drift from what the page actually shows.
 * Rows with no allocation date (val calls / WIP) are ignored.
 */
export function oldestQueueAgeMinutes(
  applications: { allocatedOn: string | null }[],
  now: number = Date.now()
): number | null {
  let oldest: number | null = null;
  for (const app of applications) {
    if (!app.allocatedOn) continue;
    const minutes = (now - new Date(app.allocatedOn).getTime()) / 60_000;
    if (!Number.isFinite(minutes) || minutes < 0) continue;
    if (oldest == null || minutes > oldest) oldest = minutes;
  }
  return oldest;
}

/** Whole-number percentage of `value` within `total`; 0 when there is no total. */
export function percentOf(value: number, total: number): number {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

/**
 * Claims the agent opened but did not see through. Falls back to deriving it
 * from opened-minus-actioned when the API has not supplied the figure, which
 * can undercount if the agent actioned something they claimed in an earlier
 * period — hence never below zero.
 */
export function unactionedFor(agent: UWQueueAgentRow): number {
  if (agent.unactioned != null) return agent.unactioned;
  return Math.max(0, agent.opened - agent.totalActioned);
}

export interface QueueOutcomeSplit {
  allocated: number;
  deaded: number;
  allocatedPct: number;
  deadedPct: number;
  /** Left the queue in the period with no action recorded in the portal. */
  outside: number;
}

/**
 * Allocated vs deaded split. The API supplies the percentages, but they are
 * recomputed here when absent so the card never renders a blank next to a count.
 */
export function outcomeSplit(stats: UWQueueDashboardStats): QueueOutcomeSplit {
  const total = stats.actionedCount || stats.allocatedCount + stats.deadedCount;
  return {
    allocated: stats.allocatedCount,
    deaded: stats.deadedCount,
    allocatedPct: stats.allocatedPct ?? percentOf(stats.allocatedCount, total),
    deadedPct: stats.deadedPct ?? percentOf(stats.deadedCount, total),
    outside: stats.outsideQueueCount ?? 0,
  };
}

/** Column totals for the team table footer. */
export function agentTotals(agents: UWQueueAgentRow[]) {
  return agents.reduce(
    (acc, a) => ({
      opened: acc.opened + a.opened,
      allocated: acc.allocated + a.allocated,
      deaded: acc.deaded + a.deaded,
      totalActioned: acc.totalActioned + a.totalActioned,
      unactioned: acc.unactioned + unactionedFor(a),
    }),
    { opened: 0, allocated: 0, deaded: 0, totalActioned: 0, unactioned: 0 }
  );
}
